import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import UserDetailSheet from "@/components/UserDetailSheet";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Filter, Search, Plane, HeartPulse, GraduationCap,
  Sun, Ban, Coffee, ArrowLeftRight, Trash2, Save, X, MapPin, Users as UsersIcon,
} from "lucide-react";

const AREAS = ["Handverkauf", "Rezeptur", "Labor", "Telefon/Backoffice", "Botendienst", "Notdienst"];
const POSITIONS = ["Apotheker/in", "PTA", "PKA", "Botenfahrer", "Reinigungskraft", "Student", "Praktikant", "Filialleiter"];
const ABS_META = {
  vacation: { label: "Urlaub", icon: Plane, color: "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300" },
  sick: { label: "Krank", icon: HeartPulse, color: "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300" },
  training: { label: "Fortbildung", icon: GraduationCap, color: "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300" },
  special: { label: "Sonderurlaub", icon: Sun, color: "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300" },
  maternity: { label: "Elternzeit", icon: Sun, color: "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300" },
};

const WEEKDAY_KEYS = ["mo", "di", "mi", "do", "fr", "sa", "so"];
const WEEKDAY_LABELS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

const startOfWeek = (d) => {
  const day = d.getDay() || 7;
  const nd = new Date(d);
  if (day !== 1) nd.setDate(d.getDate() - day + 1);
  nd.setHours(0, 0, 0, 0);
  return nd;
};
const iso = (d) => d.toISOString().slice(0, 10);
const minutesFromTimes = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let m = (eh * 60 + em) - (sh * 60 + sm);
  if (m < 0) m += 24 * 60;
  return m;
};
const formatDelta = (mins) => {
  const sign = mins >= 0 ? "+" : "-";
  const abs = Math.abs(Math.round(mins));
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};
const formatHM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(Math.round(mins) % 60).padStart(2, "0")}`;

export default function SchedulePage() {
  const { user, isManager } = useAuth();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState([]); // empty = all
  const [posFilter, setPosFilter] = useState([]); // empty = all
  const [search, setSearch] = useState("");
  const [expandedLoc, setExpandedLoc] = useState({}); // { loc_id: bool }
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null); // "userId_date"
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleShiftDragStart = (e, s) => {
    e.stopPropagation();
    e.dataTransfer.setData("application/x-shift-id", s.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleCellDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverCell !== key) setDragOverCell(key);
  };
  const handleCellDragLeave = () => setDragOverCell(null);
  const handleCellDrop = async (e, targetUser, targetDate) => {
    e.preventDefault();
    setDragOverCell(null);
    const shiftId = e.dataTransfer.getData("application/x-shift-id");
    if (!shiftId) return;
    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return;
    if (shift.user_id === targetUser.id && shift.date === iso(targetDate)) return;
    try {
      await api.put(`/schedules/${shiftId}`, {
        ...shift,
        user_id: targetUser.id,
        date: iso(targetDate),
      });
      toast.success("Schicht verschoben");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verschieben fehlgeschlagen");
    }
  };

  const days = useMemo(() => Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d;
  }), [weekStart]);

  const load = async () => {
    const from = iso(days[0]);
    const to = iso(days[6]);
    const [rs, ru, rl, ra] = await Promise.all([
      api.get(`/schedules?from_date=${from}&to_date=${to}`),
      isManager ? api.get("/users") : api.get("/users").catch(() => ({ data: [] })),
      api.get("/locations"),
      api.get(`/absences${isManager ? "?status=approved" : ""}`).catch(() => ({ data: [] })),
    ]);
    setShifts(rs.data);
    setUsers(ru.data);
    setLocations(rl.data);
    setAbsences(ra.data.filter(a => a.status === "approved" || a.status === "pending"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  useEffect(() => {
    // Initially expand all locations
    if (locations.length && Object.keys(expandedLoc).length === 0) {
      setExpandedLoc(Object.fromEntries(locations.map(l => [l.id, true])));
    }
    // eslint-disable-next-line
  }, [locations]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (u.role === "kiosk") return false;
      if (!u.active) return false;
      if (posFilter.length && !posFilter.includes(u.position)) return false;
      if (locFilter.length && !(u.location_ids || []).some(id => locFilter.includes(id))) return false;
      if (q) {
        const hay = `${u.first_name} ${u.last_name} ${u.username}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, posFilter, locFilter]);

  const groupedByLocation = useMemo(() => {
    const map = new Map();
    for (const u of filteredUsers) {
      const locId = u.primary_location_id || (u.location_ids || [])[0] || "unassigned";
      if (!map.has(locId)) map.set(locId, {});
      const posMap = map.get(locId);
      const pos = u.position || "Ohne Position";
      if (!posMap[pos]) posMap[pos] = [];
      posMap[pos].push(u);
    }
    // Return array of { location, positions: [ {name, users} ] }
    const out = [];
    for (const [locId, posMap] of map.entries()) {
      const location = locations.find(l => l.id === locId) || { id: locId, name: "Ohne Standort", color: "#94a3b8" };
      if (locFilter.length && !locFilter.includes(locId)) continue;
      const positions = Object.entries(posMap)
        .map(([name, arr]) => ({ name, users: arr.sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "")) }))
        .sort((a, b) => a.name.localeCompare(b.name));
      out.push({ location, positions });
    }
    return out.sort((a, b) => a.location.name.localeCompare(b.location.name));
  }, [filteredUsers, locations, locFilter]);

  const shiftsByUserDate = useMemo(() => {
    const map = {};
    for (const s of shifts) {
      const key = `${s.user_id}_${s.date}`;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    return map;
  }, [shifts]);

  const absencesByUserDate = useMemo(() => {
    const map = {};
    for (const a of absences) {
      let cur = new Date(a.start_date);
      const end = new Date(a.end_date);
      while (cur <= end) {
        const key = `${a.user_id}_${iso(cur)}`;
        if (!map[key]) map[key] = [];
        map[key].push(a);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [absences]);

  const weekMetrics = (u) => {
    let actual = 0, target = 0;
    for (const [i, d] of days.entries()) {
      const ds = iso(d);
      target += (u.work_hours?.[WEEKDAY_KEYS[i]] || 0) * 60;
      const s = shiftsByUserDate[`${u.id}_${ds}`] || [];
      const abs = absencesByUserDate[`${u.id}_${ds}`] || [];
      if (abs.length > 0) {
        actual += (u.work_hours?.[WEEKDAY_KEYS[i]] || 0) * 60;
      } else {
        for (const sh of s) actual += minutesFromTimes(sh.start_time, sh.end_time);
      }
    }
    return { actual, target, delta: actual - target };
  };

  const openShiftEdit = (u, date, existing) => {
    const dateStr = iso(date);
    const weekday = date.getDay() === 0 ? 6 : date.getDay() - 1;
    const targetH = u.work_hours?.[WEEKDAY_KEYS[weekday]] || 0;
    if (existing) {
      setForm({
        _mode: "edit", _dateLabel: date, id: existing.id,
        mode: "work",
        user_id: existing.user_id, location_id: existing.location_id, date: dateStr,
        start_time: existing.start_time || "08:00",
        end_time: existing.end_time || "16:00",
        area: existing.area || "Handverkauf",
        role_required: existing.role_required || u.position || "",
        notes: existing.notes || "",
      });
    } else {
      setForm({
        _mode: "create", _dateLabel: date,
        mode: "work",
        user_id: u.id,
        location_id: u.primary_location_id || locations[0]?.id,
        date: dateStr,
        start_time: targetH > 0 ? "08:00" : "09:00",
        end_time: targetH > 0 ? `${String(Math.min(23, 8 + Math.ceil(targetH))).padStart(2, "0")}:00` : "17:00",
        area: "Handverkauf",
        role_required: u.position || "",
        notes: "",
        absence_type: "vacation",
        absence_reason: "",
      });
    }
    setDialog(true);
  };

  const saveShift = async () => {
    if (!form) return;
    try {
      if (form.mode === "work") {
        const payload = {
          user_id: form.user_id, location_id: form.location_id, date: form.date,
          start_time: form.start_time, end_time: form.end_time,
          area: form.area, role_required: form.role_required, notes: form.notes,
          is_open_shift: !form.user_id,
        };
        if (form._mode === "edit") await api.put(`/schedules/${form.id}`, payload);
        else await api.post("/schedules", payload);
        toast.success("Schicht gespeichert");
      } else if (form.mode === "absence") {
        // Insert via personnel/day-entry (works for past dates, admin-created)
        await api.post("/personnel/day-entry", {
          user_id: form.user_id, date: form.date, mode: "absence",
          absence_type: form.absence_type, reason: form.absence_reason,
        });
        toast.success("Abwesenheit eingetragen");
      } else if (form.mode === "free") {
        await api.post("/personnel/day-entry", { user_id: form.user_id, date: form.date, mode: "free" });
        toast.success("Als freier Tag markiert");
        if (form._mode === "edit" && form.id) {
          try { await api.delete(`/schedules/${form.id}`); } catch (_) { /* ignore */ }
        }
      }
      setDialog(false);
      setForm(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const deleteShift = async () => {
    if (!form?.id) { setDialog(false); return; }
    if (!window.confirm("Schicht wirklich löschen?")) return;
    try {
      await api.delete(`/schedules/${form.id}`);
      toast.success("Gelöscht");
      setDialog(false); setForm(null); await load();
    } catch (err) { toast.error("Fehler"); }
  };

  const offerSwap = async (shiftId) => {
    try { await api.post("/schedules/swap-request", { shift_id: shiftId }); toast.success("Zum Tausch angeboten"); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const clearFilters = () => { setLocFilter([]); setPosFilter([]); setSearch(""); };
  const activeFilterCount = locFilter.length + posFilter.length + (search ? 1 : 0);
  const monthLabel = days[3].toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4" data-testid="schedule-root">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <div className="overline">Einsatzplan</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold capitalize">{monthLabel}</h2>
          <p className="text-sm text-slate-500 mt-1">Klicke auf eine Zelle, um eine Schicht, Abwesenheit oder Frei-Tag einzutragen.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="sched-search" placeholder="Mitarbeiter…" className="pl-9 w-52" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="sched-filter-btn" variant="outline" className="gap-2">
                <Filter className="w-4 h-4" /> Filter
                {activeFilterCount > 0 && <Badge variant="secondary" className="ml-1">{activeFilterCount}</Badge>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-4" align="end">
              <div>
                <div className="text-xs font-heading font-bold uppercase tracking-wider text-slate-500 mb-2">Standorte</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {locations.map(l => (
                    <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        data-testid={`sched-filter-loc-${l.id}`}
                        checked={locFilter.includes(l.id)}
                        onCheckedChange={(v) => setLocFilter(v ? [...locFilter, l.id] : locFilter.filter(x => x !== l.id))}
                      />
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
                      {l.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-heading font-bold uppercase tracking-wider text-slate-500 mb-2">Berufe</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {POSITIONS.map(p => (
                    <label key={p} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        data-testid={`sched-filter-pos-${p}`}
                        checked={posFilter.includes(p)}
                        onCheckedChange={(v) => setPosFilter(v ? [...posFilter, p] : posFilter.filter(x => x !== p))}
                      />
                      {p}
                    </label>
                  ))}
                </div>
              </div>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full gap-2"><X className="w-3.5 h-3.5" /> Zurücksetzen</Button>
              )}
            </PopoverContent>
          </Popover>
          <Button data-testid="sched-prev" variant="outline" size="icon" onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * 86400000))}><ChevronLeft className="w-4 h-4" /></Button>
          <Button data-testid="sched-today" variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))}>Heute</Button>
          <Button data-testid="sched-next" variant="outline" size="icon" onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * 86400000))}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Card className="border-border/60 overflow-x-auto">
        <CardContent className="p-0">
          <div className="min-w-[900px]">
            {/* Header row: weekdays */}
            <div className="grid grid-cols-[minmax(240px,1fr)_repeat(7,minmax(120px,1fr))] border-b border-border/60 bg-slate-50 dark:bg-slate-900 sticky top-0 z-10">
              <div className="px-4 py-3 flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wider text-slate-500">
                <MapPin className="w-3.5 h-3.5" /> Standorte
              </div>
              {days.map((d, i) => {
                const isToday = iso(d) === iso(new Date());
                return (
                  <div key={i} className={`px-3 py-3 text-center border-l border-border/60 ${isToday ? "bg-blue-50 dark:bg-blue-950/30" : ""}`}>
                    <div className={`text-xs ${isToday ? "text-blue-700 dark:text-blue-400 font-bold" : "text-slate-500"}`}>{WEEKDAY_LABELS[i]}</div>
                    <div className={`font-heading text-2xl font-bold ${isToday ? "text-blue-700 dark:text-blue-400" : ""}`}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>

            {/* Rows */}
            {groupedByLocation.length === 0 && (
              <div className="text-center text-slate-500 py-16 text-sm">Keine Mitarbeiter für den gewählten Filter.</div>
            )}
            {groupedByLocation.map(({ location, positions }) => (
              <div key={location.id}>
                {/* Location group header */}
                <button
                  data-testid={`sched-loc-${location.id}`}
                  onClick={() => setExpandedLoc({ ...expandedLoc, [location.id]: !expandedLoc[location.id] })}
                  className="w-full grid grid-cols-[minmax(240px,1fr)_repeat(7,minmax(120px,1fr))] items-center text-left border-b-2 hover:brightness-95 transition"
                  style={{ backgroundColor: `${location.color}22`, borderColor: location.color }}
                >
                  <div className="px-4 py-2 font-heading font-bold flex items-center gap-2" style={{ color: location.color }}>
                    <span>{expandedLoc[location.id] === false ? "▶" : "▼"}</span>
                    {location.name}
                  </div>
                  <div className="col-span-7" />
                </button>

                {expandedLoc[location.id] !== false && positions.map(({ name, users: posUsers }) => (
                  <React.Fragment key={`${location.id}_${name}`}>
                    <div className="grid grid-cols-[minmax(240px,1fr)_repeat(7,minmax(120px,1fr))] bg-slate-50/50 dark:bg-slate-900/40 border-b border-border/40">
                      <div className="px-4 py-1.5 text-xs font-bold text-slate-500 flex items-center gap-1.5">
                        <UsersIcon className="w-3 h-3" /> {name}
                      </div>
                      <div className="col-span-7" />
                    </div>
                    {posUsers.map(u => {
                      const m = weekMetrics(u);
                      const target = m.target;
                      const actual = m.actual;
                      const delta = m.delta;
                      const isPositive = delta >= 0;
                      return (
                        <div key={u.id} data-testid={`sched-user-row-${u.id}`} className="grid grid-cols-[minmax(240px,1fr)_repeat(7,minmax(120px,1fr))] border-b border-border/40 hover:bg-slate-50/40 dark:hover:bg-slate-900/30">
                          <div className="px-4 py-3">
                            <div className="flex items-baseline justify-between gap-2">
                              <button
                                data-testid={`sched-name-${u.id}`}
                                onClick={() => { setSelectedUserId(u.id); setDetailOpen(true); }}
                                className="font-semibold truncate text-left hover:underline underline-offset-2"
                                title="Details anzeigen"
                              >
                                {u.last_name}, {u.first_name}
                              </button>
                              <div className={`text-xs font-bold tabular-nums ${isPositive ? "text-blue-700 dark:text-blue-400" : "text-rose-600"}`}>{formatDelta(delta)}</div>
                            </div>
                            <div className="flex items-baseline justify-between gap-2 mt-0.5">
                              <div className="text-xs text-slate-500">{u.position}</div>
                              <div className="text-[10px] text-slate-500 tabular-nums">{formatHM(actual)} / {formatHM(target)}h</div>
                            </div>
                          </div>
                          {days.map((d, di) => {
                            const ds = iso(d);
                            const dayShifts = shiftsByUserDate[`${u.id}_${ds}`] || [];
                            const dayAbs = absencesByUserDate[`${u.id}_${ds}`] || [];
                            const abs = dayAbs[0];
                            const canEdit = isManager;
                            const targetHours = u.work_hours?.[WEEKDAY_KEYS[di]] || 0;
                            const cellKey = `${u.id}_${ds}`;
                            const isDragTarget = dragOverCell === cellKey;
                            return (
                              <div
                                key={ds}
                                data-testid={`sched-cell-${u.id}-${ds}`}
                                onClick={() => canEdit && openShiftEdit(u, d, dayShifts[0])}
                                onDragOver={canEdit && isManager ? (e) => handleCellDragOver(e, cellKey) : undefined}
                                onDragLeave={canEdit && isManager ? handleCellDragLeave : undefined}
                                onDrop={canEdit && isManager ? (e) => handleCellDrop(e, u, d) : undefined}
                                className={`border-l border-border/40 min-h-[64px] p-1.5 cursor-pointer transition-colors ${canEdit ? "hover:bg-blue-50/40 dark:hover:bg-blue-950/20" : ""} ${isDragTarget ? "ring-2 ring-inset ring-blue-500 bg-blue-100/60 dark:bg-blue-900/40" : ""}`}
                              >
                                {abs ? (
                                  <div className={`h-full min-h-[52px] rounded-md flex items-center justify-center gap-1 text-xs font-medium ${ABS_META[abs.type]?.color || "bg-slate-200 text-slate-700"}`}>
                                    {React.createElement(ABS_META[abs.type]?.icon || Plane, { className: "w-3.5 h-3.5" })}
                                    <span>{ABS_META[abs.type]?.label || abs.type}</span>
                                  </div>
                                ) : dayShifts.length ? (
                                  dayShifts.map(s => {
                                    const dur = minutesFromTimes(s.start_time, s.end_time);
                                    return (
                                      <div
                                        key={s.id}
                                        draggable={isManager}
                                        onDragStart={(e) => handleShiftDragStart(e, s)}
                                        className={`rounded-md px-2 py-1.5 text-xs relative group ${isManager ? "cursor-grab active:cursor-grabbing" : ""}`}
                                        style={{ backgroundColor: `${location.color}22`, borderLeft: `3px solid ${location.color}` }}
                                        title={isManager ? "Zum Verschieben ziehen" : ""}
                                      >
                                        <div className="flex justify-between items-baseline">
                                          <div className="font-semibold tabular-nums">{s.start_time}–{s.end_time}</div>
                                          <div className="text-[10px] text-slate-500 tabular-nums">{formatHM(dur)}h</div>
                                        </div>
                                        <div className="w-full h-0.5 rounded-full mt-1" style={{ backgroundColor: location.color }} />
                                        {s.swap_request_user_id && <div className="text-[10px] text-amber-700 font-semibold mt-0.5 flex items-center gap-0.5"><ArrowLeftRight className="w-2.5 h-2.5" /> Tausch</div>}
                                        {s.user_id === user.id && !s.swap_request_user_id && (
                                          <button
                                            title="Zum Tausch anbieten"
                                            className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={(e) => { e.stopPropagation(); offerSwap(s.id); }}
                                          >
                                            <ArrowLeftRight className="w-3 h-3 text-slate-500 hover:text-blue-700" />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className={`h-full min-h-[52px] rounded-md border border-dashed ${targetHours > 0 ? "border-border/40" : "border-transparent"} flex items-center justify-center text-[10px] text-slate-300 dark:text-slate-700`}>
                                    {targetHours > 0 ? "+" : ""}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Shift Editor Dialog */}
      <Dialog open={dialog} onOpenChange={(o) => { if (!o) { setDialog(false); setForm(null); } }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{form?._dateLabel?.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</span>
              <Badge variant="outline">{users.find(u => u.id === form?.user_id)?.first_name} {users.find(u => u.id === form?.user_id)?.last_name}</Badge>
            </DialogTitle>
            <DialogDescription>
              Änderungen werden im Audit-Log protokolliert. Auch vergangene Tage können bearbeitet werden.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <>
              <div>
                <Label>Modus</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <Button data-testid="shift-mode-work" size="sm" variant={form.mode === "work" ? "default" : "outline"} onClick={() => setForm({ ...form, mode: "work" })}><Coffee className="w-3.5 h-3.5 mr-1" /> Arbeit</Button>
                  <Button data-testid="shift-mode-absence" size="sm" variant={form.mode === "absence" ? "default" : "outline"} onClick={() => setForm({ ...form, mode: "absence" })}><Plane className="w-3.5 h-3.5 mr-1" /> Abwesenheit</Button>
                  <Button data-testid="shift-mode-free" size="sm" variant={form.mode === "free" ? "default" : "outline"} onClick={() => setForm({ ...form, mode: "free" })}><Ban className="w-3.5 h-3.5 mr-1" /> Frei</Button>
                </div>
              </div>

              {form.mode === "work" && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div><Label>Beginn</Label><Input type="time" data-testid="shift-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                  <div><Label>Ende</Label><Input type="time" data-testid="shift-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
                  <div>
                    <Label>Standort</Label>
                    <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                      <SelectTrigger data-testid="shift-loc"><SelectValue /></SelectTrigger>
                      <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Bereich</Label>
                    <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Notiz (optional)</Label>
                    <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Öffentliche Notiz für die Schicht…" />
                  </div>
                  <div className="col-span-2 text-xs text-slate-500 tabular-nums">
                    Dauer: {formatHM(minutesFromTimes(form.start_time, form.end_time))}h
                  </div>
                </div>
              )}

              {form.mode === "absence" && (
                <div className="space-y-3 mt-2">
                  <div>
                    <Label>Art der Abwesenheit</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      {Object.entries(ABS_META).map(([id, m]) => (
                        <Button key={id} data-testid={`shift-abs-${id}`} size="sm" variant={form.absence_type === id ? "default" : "outline"} onClick={() => setForm({ ...form, absence_type: id })}>
                          <m.icon className="w-3.5 h-3.5 mr-1" /> {m.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Notiz / Begründung</Label>
                    <Input value={form.absence_reason} onChange={(e) => setForm({ ...form, absence_reason: e.target.value })} placeholder="optional" />
                  </div>
                </div>
              )}

              {form.mode === "free" && (
                <div className="text-sm text-slate-500 mt-2 p-3 rounded-md border border-border/60 bg-slate-50 dark:bg-slate-900/40">
                  Der Tag wird als freier Tag markiert. Bestehende Zeiterfassungen und Abwesenheiten dieses Tages werden entfernt.
                </div>
              )}
            </>
          )}
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            {form?._mode === "edit" && form?.mode === "work" && (
              <Button variant="ghost" onClick={deleteShift} className="text-rose-600 hover:text-rose-700 sm:mr-auto"><Trash2 className="w-4 h-4 mr-1" /> Schicht löschen</Button>
            )}
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button data-testid="shift-save" onClick={saveShift}><Save className="w-4 h-4 mr-2" /> Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserDetailSheet
        userId={selectedUserId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        locations={locations}
        onChanged={load}
      />
    </div>
  );
}
